import { supabase } from '../utils/supabase';
import { trackEvent, getGaAttribution } from '../utils/analytics';

/**
 * `functions.invoke` collapses every non-2xx into the same opaque
 * "Edge Function returned a non-2xx status code" and leaves the JSON body in
 * `error.context`. Our billing functions answer with `{ error, code }`, so read
 * the body and re-throw something a human and a caller can both act on.
 */
export class CheckoutError extends Error {
    code?: string;
    constructor(message: string, code?: string) {
        super(message);
        this.name = 'CheckoutError';
        this.code = code;
    }
}

async function toCheckoutError(error: any): Promise<CheckoutError> {
    try {
        const body = await error?.context?.json?.();
        if (body?.error) return new CheckoutError(body.error, body.code);
    } catch {
        /* body already consumed or not JSON — fall back to the generic message */
    }
    return new CheckoutError(error?.message || 'Checkout failed', undefined);
}

export async function createCheckoutSession(
    planId: string,
    options: { skipTrial?: boolean; source?: string } = {},
) {
    const { data } = await supabase.auth.getSession();

    if (!data.session) {
        throw new Error('User not authenticated');
    }

    // Analytics: mark checkout intent and preserve the GA client_id in Stripe
    // metadata for future server-side purchase attribution. Never let
    // analytics block checkout.
    trackEvent('begin_checkout', { plan: planId, skip_trial: options.skipTrial === true, surface: options.source || 'unknown' });
    const ga = await getGaAttribution().catch(() => ({ clientId: null, sessionId: null }));

    const { data: response, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
            planId,
            skipTrial: options.skipTrial === true,
            source: options.source || 'web',
            gaClientId: ga.clientId,
            gaSessionId: ga.sessionId,
            successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/payment/cancelled`,
        },
    });

    if (error) {
        const checkoutError = await toCheckoutError(error);
        console.error('Error creating checkout session:', checkoutError.code || 'unknown', checkoutError.message);
        throw checkoutError;
    }

    if (response?.url) {
        window.location.href = response.url;
    } else {
        throw new CheckoutError('No checkout URL returned');
    }
}

/**
 * Opens the Stripe billing portal where the user can update payment details,
 * switch plans, or cancel their subscription.
 */
export async function createPortalSession() {
    const { data } = await supabase.auth.getSession();

    if (!data.session) {
        throw new Error('User not authenticated');
    }

    const { data: response, error } = await supabase.functions.invoke('create-portal-session', {
        body: {},
    });

    if (error) {
        const portalError = await toCheckoutError(error);
        console.error('Error creating portal session:', portalError.code || 'unknown', portalError.message);
        throw portalError;
    }

    if (response?.url) {
        window.location.href = response.url;
    } else {
        throw new CheckoutError('No portal URL returned');
    }
}
